import axios from "axios";
import { axiosClient } from "../api/axiosClient";
import { currentAppPublicOrigin } from "../utils/appPublicUrl";

export interface QRCodeInvite {
  id: string;
  code: string;
  url: string;
  status: "in-progress" | "expired" | "revoked";
  expiresAt: string;
  createdAt: string;
  scannedCount: number;
  successfulUses: number;
  usageMode: "single_use" | "multi_until_expiry";
  usedBy?: string;
  terreiroId: string;
}

export interface ResolveInviteResponse {
  invite: {
    code: string;
    expiresAt: string;
    usageMode: "single_use" | "multi_until_expiry";
  };
  terreiro: {
    id: string;
    name: string;
    imageUrl: string | null;
    privacyPolicyUrl: string | null;
    policyContent: {
      source: "SYSTEM" | "CUSTOM";
      key: string;
      title: string;
      markdown: string;
      version?: number;
      updatedAt?: string;
    } | null;
    religion: { id: string; name: string } | null;
  };
  monthlyFee: { monthlyFee: number; dueDay: number } | null;
}

export const qrcodeInviteRepository = {
  async createInvite(
    terreiroId: string,
    options?: {
      ttlHours?: number;
      usageMode?: "single_use" | "multi_until_expiry";
    },
  ): Promise<QRCodeInvite> {
    let attempts = 0;
    const maxAttempts = 3;
    while (attempts < maxAttempts) {
      try {
        const response = await axiosClient.post(
          `/qrcode-invite`,
          {
            terreiroId,
            ...(options?.ttlHours ? { ttlHours: options.ttlHours } : {}),
            ...(options?.usageMode ? { usageMode: options.usageMode } : {}),
            ...(currentAppPublicOrigin()
              ? { frontendOrigin: currentAppPublicOrigin() }
              : {}),
          },
          { withCredentials: true },
        );
        return response.data;
      } catch (error: unknown) {
        if (
          axios.isAxiosError(error) &&
          error.response?.status &&
          error.response.status >= 500
        ) {
          attempts++;
          await new Promise((resolve) =>
            setTimeout(resolve, 2 ** attempts * 1000),
          );
          if (attempts === maxAttempts)
            throw new Error("Max retry attempts reached");
        } else {
          throw error;
        }
      }
    }
    throw new Error("Failed to create QR code invite");
  },

  async getInvite(code: string): Promise<QRCodeInvite> {
    const response = await axiosClient.get(`/qrcode-invite/${code}`, {
      withCredentials: true,
    });
    return response.data;
  },

  async trackInviteOpen(code: string): Promise<void> {
    await axiosClient.post(`/qrcode-invite/${code}/open`);
  },

  async useInvite(code: string, userId: string): Promise<QRCodeInvite> {
    const response = await axiosClient.patch(
      `/qrcode-invite/${code}/use`,
      { userId },
      { withCredentials: true },
    );
    return response.data;
  },

  async revokeInvite(code: string): Promise<QRCodeInvite> {
    const response = await axiosClient.patch(
      `/qrcode-invite/${code}/revoke`,
      null,
      {
        withCredentials: true,
      },
    );
    return response.data;
  },

  /**
   * Fetch the first in-progress QR code invite for a given terreiroId.
   * Throws if not found (404).
   */
  async getFirstInProgressInvite(
    terreiroId: string,
  ): Promise<QRCodeInvite | undefined> {
    const response = await axiosClient.get(
      `/qrcode-invite?terreiroId=${encodeURIComponent(
        terreiroId,
      )}&status=in-progress`,
      { withCredentials: true },
    );
    // API should return either the invite or 404
    return response.data;
  },

  /**
   * Resolve an invite code — public endpoint, returns terreiro info + monthly fee.
   */
  async resolveInvite(code: string): Promise<ResolveInviteResponse> {
    const response = await axiosClient.get(`/qrcode-invite/${code}/resolve`);
    return response.data;
  },
};
